import React, { useState } from 'react';
import axios from 'axios';

const FreelancerBlockPage = () => {
  const [searchValue, setSearchValue] = useState('');
  const [freelancerInfo, setFreelancerInfo] = useState(null);
  const [statusHistory, setStatusHistory] = useState([]);
  const [blockReason, setBlockReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  const [message, setMessage] = useState('');

  const apiBaseURL = process.env.REACT_APP_API_BASE_URL;

  const handleSearch = async () => {
    if (!searchValue || isNaN(searchValue)) {
      setMessage('❗ Please enter a valid Freelancer ID (number).');
      return;
    }

    try {
      const params = { freelancer_id: parseInt(searchValue) };
      const [infoRes, historyRes] = await Promise.all([
        axios.get(`${apiBaseURL}/api/freelancer/get-freelancer-account-info`, { params }),
        axios.get(`${apiBaseURL}/api/freelancer/get-freelancer-account-status-history`, { params })
      ]);
      setFreelancerInfo(infoRes.data);
      setStatusHistory(historyRes.data);
      setMessage('');
    } catch (error) {
      console.error(error);
      setFreelancerInfo(null);
      setStatusHistory([]);
      setMessage('❌ Freelancer not found.');
    }
  };

  const getBlockReason = () => {
    if (customReason.trim()) return customReason.trim();
    return blockReason.trim();
  };

  const handleBlock = async () => {
    if (!freelancerInfo) return;

    if (freelancerInfo.account_status === 'Blocked') {
      alert('⚠️ Freelancer is already blocked.');
      return;
    }

    const reason = getBlockReason();
    if (!reason) {
      alert('❗ Please select or type a block reason.');
      return;
    }

    const confirmed = window.confirm(`Are you sure you want to block this freelancer for:\n\n"${reason}"`);
    if (!confirmed) return;

    try {
      await axios.post(`${apiBaseURL}/api/freelancer/block-freelancer`, {
        freelancer_id: freelancerInfo.freelancer_id,
        blocked_reason: reason
      });
      alert('✅ Freelancer blocked successfully.');
      handleSearch();
    } catch (error) {
      console.error(error);
      alert('❌ Error blocking freelancer.');
    }
  };

  const handleUnblock = async () => {
    if (!freelancerInfo) return;

    if (freelancerInfo.account_status === 'Active') {
      alert('ℹ️ Freelancer is already active.');
      return;
    }

    const confirmed = window.confirm('Are you sure you want to unblock this freelancer?');
    if (!confirmed) return;

    try {
      const response = await axios.post(`${apiBaseURL}/api/freelancer/unblock-freelancer`, {
        freelancer_id: freelancerInfo.freelancer_id
      });
      alert(response.data.message || '✅ Freelancer unblocked.');
      handleSearch();
    } catch (error) {
      console.error(error);
      alert('❌ Error unblocking freelancer.');
    }
  };

  return (
    <div className="p-6" style={{ marginLeft: '16rem' }}>
      <h2 className="text-2xl font-semibold mb-4">Block/Unblock Freelancer</h2>

      <div className="flex gap-4 items-end mb-6">
        <div>
          <label className="block mb-1">Freelancer ID</label>
          <input
            type="text"
            className="border p-2 rounded w-full"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder="Enter freelancer ID"
          />
        </div>
        <button className="bg-blue-600 text-white px-4 py-2 rounded" onClick={handleSearch}>
          Search
        </button>
      </div>

      {freelancerInfo && (
        <div className="border p-4 rounded mb-4 bg-gray-50">
          <h3 className="text-xl font-semibold mb-2">Freelancer Info</h3>
          <p><strong>ID:</strong> {freelancerInfo.freelancer_id}</p>
          <p><strong>Name:</strong> {freelancerInfo.name}</p>
          <p><strong>Email:</strong> {freelancerInfo.email}</p>
          <p><strong>Phone:</strong> {freelancerInfo.phone}</p>
          <p><strong>Status:</strong> {freelancerInfo.account_status}</p>
          <p><strong>Blocked Reason:</strong> {freelancerInfo.blocked_reason || 'N/A'}</p>

          <div className="mt-4">
            <label className="block mb-1">Select a Block Reason</label>
            <select
              className="border p-2 rounded w-full"
              value={blockReason}
              onChange={(e) => setBlockReason(e.target.value)}
            >
              <option value="">Select reason...</option>
              <option value="Violation of platform policies">Violation of platform policies</option>
              <option value="Used influencer subsidy but did not post content">Used influencer subsidy but did not post content</option>
              <option value="Misuse of withdrawal feature">Misuse of withdrawal feature</option>
            </select>
          </div>

          <div className="mt-2">
            <label className="block mb-1">Or Type Custom Reason</label>
            <input
              type="text"
              placeholder="Enter custom reason..."
              className="border p-2 rounded w-full"
              value={customReason}
              onChange={(e) => setCustomReason(e.target.value)}
              maxLength={300}
            />
          </div>

          <div className="flex gap-4 mt-4">
            <button className="bg-red-600 text-white px-4 py-2 rounded" onClick={handleBlock}>
              Block
            </button>
            <button className="bg-green-600 text-white px-4 py-2 rounded" onClick={handleUnblock}>
              Unblock
            </button>
          </div>
        </div>
      )}

      {statusHistory.length > 0 && (
        <div className="mt-6">
          <h4 className="text-lg font-semibold mb-2">Account Status History</h4>
          <table className="min-w-full bg-white border border-gray-300 text-sm">
            <thead>
              <tr className="bg-gray-100">
                <th className="border px-4 py-2 text-left">#</th>
                <th className="border px-4 py-2 text-left">Status</th>
                <th className="border px-4 py-2 text-left">Reason</th>
                <th className="border px-4 py-2 text-left">Changed At</th>
              </tr>
            </thead>
            <tbody>
              {statusHistory.map((entry, idx) => (
                <tr key={entry.history_id || idx}>
                  <td className="border px-4 py-2">{idx + 1}</td>
                  <td className="border px-4 py-2">{entry.account_status}</td>
                  <td className="border px-4 py-2">{entry.freeze_reason || '-'}</td>
                  <td className="border px-4 py-2">{new Date(entry.change_timestamp).toLocaleString('en-IN')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {message && <p className="text-sm mt-4 text-red-600">{message}</p>}
    </div>
  );
};

export default FreelancerBlockPage;
